import { BaseController, Rx, RxList, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { startPosCheckout } from "../../../../core/ops/cart-ops";
import { errorText, thrownText } from "../../../../core/primitives/errors";
import { type ReaderInfo, toReaderInfo, unwrap } from "../../../../core/services/stripe-terminal";

/**
 * The stages mirror the SDK's own sequence, because each one fails differently
 * and the operator needs to know which step went wrong: discovering a reader on
 * the network is a very different problem from a declined card.
 */
export type TerminalStage = "idle" | "connecting" | "ready" | "collecting" | "processing" | "succeeded" | "failed";

export class TerminalPaymentState {
  readonly stage = new Rx<TerminalStage>("idle");
  readonly readers = new RxList<ReaderInfo>();
  readonly connectedReader = new Rx<ReaderInfo | null>(null);
  readonly amountTotal = new Rx<number | null>(null);
  readonly currency = new Rx<string | null>(null);
  readonly error = new Rx<string | null>(null);
}

export abstract class TerminalPaymentActions {
  abstract connect(options?: { simulated?: boolean }): Promise<void>;
  abstract collect(customerCartId: string): Promise<void>;
  abstract cancel(): Promise<void>;
  abstract reset(): void;
  abstract close(): void;
}

export type TerminalPaymentDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "stripeTerminal">;

export interface TerminalPaymentHost {
  /** The modal was dismissed; `paid` when the card was charged. */
  onClosed(paid: boolean): void;
}

/**
 * One in-person sale on a Stripe Terminal reader, start to finish.
 *
 * A **card reader** flow, not Tap to Pay: the browser SDK cannot use the phone
 * as a reader (see `core/services/stripe-terminal.ts`). The simulated reader is
 * offered explicitly so the flow can be exercised without hardware.
 */
export class TerminalPaymentController extends BaseController implements TerminalPaymentActions {
  readonly state = new TerminalPaymentState();
  /** Bumped by every connect, collect and reset, so an answer to an abandoned attempt is dropped. */
  #seq = 0;

  constructor(
    subtree: SubtreeModel,
    readonly deps: TerminalPaymentDeps,
    readonly host: TerminalPaymentHost,
  ) {
    super();
    subtree.put(TerminalPaymentState, this.state);
    subtree.put(TerminalPaymentActions, this);
  }

  /**
   * Finds readers on the account and connects to one. `simulated` is what
   * makes this testable without hardware: Stripe's simulated reader behaves
   * like a real one all the way through a payment.
   */
  connect = async (options?: { simulated?: boolean }) => {
    const storeId = this.deps.storeRepository.shopId;
    if (!storeId) return;
    const seq = this.#restart("connecting");
    try {
      const terminal = await this.deps.stripeTerminal.get(storeId, this.deps.storeRepository.locationId);
      const discovery = unwrap(
        await terminal.discoverReaders({ simulated: options?.simulated ?? false }),
        "Could not search for readers",
      );
      if (this.#stale(seq)) return;

      const readers = discovery.discoveredReaders;
      if (!readers.length) {
        this.#fail(
          options?.simulated
            ? "No simulated reader available."
            : "No readers found. Check the reader is powered on and on the same network.",
        );
        return;
      }

      // One reader is the overwhelmingly common case, so connect straight
      // away rather than making the operator pick from a list of one.
      const target = readers[0];
      unwrap(await terminal.connectReader(target), "Could not connect to the reader");
      if (this.#stale(seq)) return;

      this.state.readers.value = readers.map(toReaderInfo);
      this.state.connectedReader.value = toReaderInfo(target);
      this.state.error.value = null;
      this.state.stage.value = "ready";
    } catch (err) {
      if (this.#stale(seq)) return;
      this.#fail(thrownText(err, "Could not connect to a reader"));
    }
  };

  /**
   * Prepares the payment server-side, then collects and processes the card.
   *
   * The cart is only marked paid by the `payment_intent.succeeded` webhook:
   * this resolving means the card was charged, not that our database has caught
   * up, which is why the page refetches rather than assuming.
   */
  collect = async (customerCartId: string) => {
    const storeId = this.deps.storeRepository.shopId;
    if (!storeId) return;
    const seq = ++this.#seq;
    try {
      this.state.stage.value = "collecting";
      this.state.error.value = null;

      const prepared = await startPosCheckout({ ...this.deps, customerCartId });
      if (this.#stale(seq)) return;
      if (!prepared.ok) {
        this.#fail(errorText(prepared.errors));
        return;
      }
      const checkout = prepared.value;
      this.state.amountTotal.value = checkout.amountTotal;
      this.state.currency.value = checkout.currency;

      const terminal = await this.deps.stripeTerminal.get(storeId, this.deps.storeRepository.locationId);
      const collected = unwrap(
        await terminal.collectPaymentMethod(checkout.paymentIntentClientSecret),
        "Could not read the card",
      );
      if (this.#stale(seq)) return;

      this.state.stage.value = "processing";
      unwrap(await terminal.processPayment(collected.paymentIntent), "The payment was not completed");
      if (this.#stale(seq)) return;

      this.state.error.value = null;
      this.state.stage.value = "succeeded";
    } catch (err) {
      if (this.#stale(seq)) return;
      this.#fail(thrownText(err, "The payment failed"));
    }
  };

  /** Aborts a collection in flight; the reader returns to its idle screen. */
  cancel = async () => {
    const storeId = this.deps.storeRepository.shopId;
    if (!storeId) return;
    const seq = ++this.#seq;
    try {
      const terminal = await this.deps.stripeTerminal.get(storeId, this.deps.storeRepository.locationId);
      await terminal.cancelCollectPaymentMethod();
    } catch {
      // Nothing in flight, or the reader already moved on. Not worth surfacing.
    }
    if (this.#stale(seq)) return;
    this.state.stage.value = this.state.connectedReader.value ? "ready" : "idle";
    this.state.error.value = null;
  };

  reset = () => {
    this.#restart("idle");
  };

  close = () => {
    const paid = this.state.stage.value === "succeeded";
    this.reset();
    this.host.onClosed(paid);
  };

  #restart(stage: TerminalStage): number {
    this.state.stage.value = stage;
    this.state.readers.value = [];
    this.state.connectedReader.value = null;
    this.state.amountTotal.value = null;
    this.state.currency.value = null;
    this.state.error.value = null;
    return ++this.#seq;
  }

  #stale(seq: number): boolean {
    return this.isDisposed || seq !== this.#seq;
  }

  #fail(message: string): void {
    this.state.stage.value = "failed";
    this.state.error.value = message;
  }
}
